import { useEffect, useRef, useState } from 'react'

/**
 * A smoothed 0..1 level from an analyser.
 *
 * Smoothed on the way out as well as inside the node, because speech is
 * spiky: the raw average makes a visualiser jitter, which reads as a fault
 * rather than as a voice. Falls to zero when there is nothing to read, so a
 * stopped microphone is still rather than frozen mid-pulse.
 */
export function useAudioLevel(
  analyser: React.RefObject<AnalyserNode | null>,
  active: boolean
): number {
  const [level, setLevel] = useState(0)
  const frame = useRef<number | null>(null)
  const smoothed = useRef(0)

  useEffect(() => {
    if (!active) {
      smoothed.current = 0
      setLevel(0)
      return
    }
    const bins = new Uint8Array(256)
    const tick = () => {
      const node = analyser.current
      if (node) {
        node.getByteFrequencyData(bins.subarray(0, node.frequencyBinCount))
        let sum = 0
        for (let i = 0; i < node.frequencyBinCount; i++) sum += bins[i]
        const average = sum / node.frequencyBinCount / 255
        // Rises fast, falls slow: a level that decays at the speed it climbs
        // flickers between syllables.
        smoothed.current =
          average > smoothed.current
            ? smoothed.current * 0.4 + average * 0.6
            : smoothed.current * 0.85 + average * 0.15
        setLevel(smoothed.current)
      }
      frame.current = requestAnimationFrame(tick)
    }
    frame.current = requestAnimationFrame(tick)
    return () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current)
      frame.current = null
    }
  }, [analyser, active])

  return level
}
